import { NO_PLOT_AREA_PROPERTY_TYPES } from 'util/Constants';
import * as yup from 'yup';

export const requestOverviewSchema = yup.object().shape({
    title: yup
        .string()
        .matches(
            /^[A-Za-z0-9\s.?!,*-]+$/,
            'Request Title must contain only letters, numbers, spaces, hyphens, periods, question marks, exclamation points, commas, and asterisks',
        )
        .required('Request Title is required'),
    description: yup
        .string()
        .matches(
            /^[A-Za-z0-9\s.?!,*-]+$/,
            'Request Description must contain only letters, numbers, spaces, hyphens, periods, question marks, exclamation points, commas, and asterisks',
        )
        .required('Request Description is required'),
});

const locationDetailsSchema = yup.object().shape({
    propertyCountry: yup.string().when('usePredefinedLocations', {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Property Country is required'),
    }),
    propertyState: yup
        .array()
        .of(yup.string())
        .when('usePredefinedLocations', {
            is: true,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.required('Property State is required'),
        }),
    propertyCity: yup
        .array()
        .of(yup.string())
        .when('usePredefinedLocations', {
            is: true,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.required('Property City is required'),
        }),
    propertyNeighborhood: yup.array().of(yup.string()).nullable(),
    definedArea: yup.string().when('usePredefinedLocations', {
        is: true,
        then: (schema) => schema.required('Defined Area is required'),
        otherwise: (schema) => schema.notRequired(),
    }),
});

const propertyDetailsSchema = yup.object().shape({
    propertyType: yup.string().required('Property Type is required'),
    minConstructionArea: yup.number().when('propertyType', {
        is: (val: string) => {
            if (!val) return false;
            const selectedTypes = val.split(', ');
            return selectedTypes.length > 0 && selectedTypes.every((type: string) => type === 'Land' || type === 'Plot');
        },
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Minimum Construction Area is required'),
    }),

    maxConstructionArea: yup.number().when('propertyType', {
        is: (val: string) => {
            if (!val) return false;
            const selectedTypes = val.split(', ');
            return selectedTypes.length > 0 && selectedTypes.every((type: string) => type === 'Land' || type === 'Plot');
        },
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Maximum Construction Area is required'),
    }),
    minPlotArea: yup.number().when('propertyType', {
        is: (val: string) => {
            if (!val) return false;
            const selectedTypes = val.split(', ');
            return selectedTypes.every((type: string) => NO_PLOT_AREA_PROPERTY_TYPES.includes(type));
        },
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Minimum Plot Area is required'),
    }),
    maxPlotArea: yup.number().when('propertyType', {
        is: (val: string) => {
            if (!val) return false;
            const selectedTypes = val.split(', ');
            return selectedTypes.every((type: string) => NO_PLOT_AREA_PROPERTY_TYPES.includes(type));
        },
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Maximum Plot Area is required'),
    }),
    minBedrooms: yup.number().when('propertyType', {
        is: (val: string) => {
            if (!val) return false;
            const selectedTypes = val.split(', ');
            return selectedTypes.length > 0 && selectedTypes.every((type: string) => type === 'Land' || type === 'Plot');
        },
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Minimum Bedroom is required'),
    }),
    maxBedrooms: yup.number().when('propertyType', {
        is: (val: string) => {
            if (!val) return false;
            const selectedTypes = val.split(', ');
            return selectedTypes.length > 0 && selectedTypes.every((type: string) => type === 'Land' || type === 'Plot');
        },
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Maximum Bedroom is required'),
    }),
});

const budgetTimelineSchema = yup.object().shape({
    minBudget: yup.number().required('Minimum Budget is required'),
    maxBudget: yup.number().required('Maximum Budget is required'),
    commission: yup.mixed().required('Commission is required'),
    urgency: yup.string().required('Urgency is required'),
});

const additionalPreferencesSchema = yup.object().shape({
    amenities: yup.array().of(yup.string()).nullable(),
    furnishing: yup.string().when('propertyType', (propertyType, schema) => {
        if (propertyType.includes('Land') || propertyType.includes('Plot')) {
            return schema.notRequired();
        }
        return schema.required('Furnishing is required');
    }),
    buyerProfile: yup.string().nullable(),
    // otherSpecificRequirements: yup.string().required('Other Specific Requirements are required'),
});

const contactInformationSchema = yup.object().shape({
    contactInfo: yup.object().shape({
        name: yup.string().required('Name is required'),
        email: yup.string().email('Invalid Email').required('Email is required'),
        phone: yup.string().required('Phone is required'),
        buyerIdentification: yup.string().optional(),
        // website: yup.string().required('Website is required'),
        // facebook: yup.string().required('Facebook is required'),
        // instagram: yup.string().required('Instagram is required'),
        // linkedin: yup.string().required('Linkedin is required'),
        // twitter: yup.string().required('Twitter is required'),
        // contactTime: yup.string().required('Contact Time is required'),
    }),
});

export const requestValidationSchemas: Record<number, yup.ObjectSchema<any>> = {
    0: requestOverviewSchema,
    1: locationDetailsSchema,
    2: propertyDetailsSchema,
    3: budgetTimelineSchema,
    4: additionalPreferencesSchema,
    5: contactInformationSchema,
};
